import React from 'react';
import { isProdEnv } from 'core/window/bootstrapConfig';
import { componentErrorHandler } from 'component/common/Hoc/ComponentErrorHandler';
import { Box } from '@drone-js/core';

interface ErrorInfo {
    componentStack: string;
}

interface ErrorBoundaryState {
    error?: Error;
    info?: ErrorInfo;
}

export function withErrorBoundary<TOriginalProps extends {}>(
    Component: React.ComponentClass<TOriginalProps> | React.FC<TOriginalProps>,
    onError = componentErrorHandler.handleComponentError,
    componentName?: string,
    maxHeight?: string,
) {
    type ComponentProps = TOriginalProps;
    // TODO: adding the correct env values.
    const isProd = isProdEnv();
    return class ErrorBoundary extends React.PureComponent<ComponentProps, ErrorBoundaryState> {
        public static displayName = `withErrorBoundary(${Component.displayName})`;

        constructor(props: ComponentProps) {
            super(props);
            this.state = {
                error: undefined,
                info: undefined,
            };
        }

        public componentDidCatch(error: Error, info: ErrorInfo) {
            if (typeof onError === 'function') {
                try {
                    const name = Component.displayName || componentName || Component.name;
                    onError.call(this, error, info ? info.componentStack : '', name);
                } catch (ignoredError) {
                    if (!isProd) {
                        console.error({ error, info });
                    }
                }
            }

            this.setState({ error, info });
        }

        public render(): React.JSX.Element | null {
            if (this.state.error) {
                if (isProd) {
                    return null;
                }
                return (
                    <Box data-testid="activities-component-error-boundary" maxHeight={maxHeight || 'auto'} overflow={maxHeight ? 'hidden' : 'auto'}>
                        {/* eslint-disable-next-line react/jsx-no-literals, @agoda/agoda-a11y/no-hardcoded-content -- react/jsx-no-literals[8e858964-566c-44c0-a231-822bb7a45380], @agoda/agoda-a11y/no-hardcoded-content[50d2266b-3d49-4285-8d12-5dca070a4aac] */}
                        <p>Error: {this.state.error.toString()}</p>
                        <pre>{this.state.info?.componentStack}</pre>
                    </Box>
                );
            }

            return <Component {...this.props} />;
        }
    };
}
